import { useState, useMemo } from 'react'
import { Stack, Button, Box, TextField, Autocomplete, Switch, Typography, Divider, IconButton, SvgIcon, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';

interface IClearableDatePickerProps {
    label : string;
    value : Dayjs | null;
    onChange : (value : Dayjs | null) => void;
    error? : boolean;
    helperText? : string;
}

function ClearableDatePicker(props : IClearableDatePickerProps) {
    return (
        <Box sx={{ position: 'relative' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                <DatePicker
                    value={props.value}
                    label={props.label}
                    onChange={(e) => props.onChange(e)}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: props.error, helperText: props.helperText } }}
                />
            </LocalizationProvider>
            { props.value && (
                <IconButton
                    size="small"
                    aria-label={`Clear ${props.label}`}
                    onClick={() => props.onChange(null)}
                    sx={{ position: 'absolute', right: 34, top: 4, padding: '4px' }}
                >
                    <SvgIcon component={CrossIcon} inheritViewBox fontSize="small" />
                </IconButton>
            )}
        </Box>
    )
}

export interface ILoadingPlanGroupDialogItem {
    Al : string;
    Dep? : string;
    DestinationGroupId? : string;
    LoadingPlanId? : string;
    HaulType? : string;
    CatererId? : string;
    IsDutyFree? : boolean;
    FlightNos? : string[];
    ExcludeFlightNos? : boolean;
    LoadingTypes? : string[];
    Waves? : number[];
    ValidFrom? : string;
    ValidTo? : string;
}

export interface ILoadingPlanGroupDialogProps {
    open : boolean;
    editItem? : ILoadingPlanGroupDialogItem;
    airlineList : {label: string, value: string}[];
    departureStationList : {label: string, value: string, airline : string}[];
    destinationGroupList : {label: string, value: string, airline : string}[];
    loadingPlanList : {label: string, value: string, airline : string}[];
    haulTypeList : {label: string, value: string, airline : string}[];
    catererList : {label: string, value: string}[];
    loadingTypeList : {label: string, value: string, airline : string}[];
    waveList : {label: string, value: number}[];
    cancelAction : () => void;
    saveAction : (airline : string, dep : string, destinationGroupId : string, loadingPlanId : string, haulType : string, catererId : string, isDutyFree : boolean, flightNos : string[], excludeFlightNos : boolean, loadingTypes : string[], waves : number[], validFrom : string | undefined, validTo : string | undefined) => void;
}

function LoadingPlanGroupDialog(props : ILoadingPlanGroupDialogProps) {
    const { t } = useTranslation();
    const isEdit = props.editItem !== undefined;

    const [ airlineCode, setAirlineCode] = useState(props.editItem?.Al ?? '');
    const [ dep, setDep] = useState(props.editItem?.Dep ?? '');
    const [ destinationGroupId, setDestinationGroupId] = useState(props.editItem?.DestinationGroupId ?? '');
    const [ loadingPlanId, setLoadingPlanId] = useState(props.editItem?.LoadingPlanId ?? '');
    const [ haulType, setHaulType] = useState(props.editItem?.HaulType ?? '');
    const [ catererId, setCatererId] = useState(props.editItem?.CatererId ?? '');
    const [ isDutyFree, setIsDutyFree] = useState(props.editItem?.IsDutyFree ?? false);
    const [ flightNos, setFlightNos] = useState((props.editItem?.FlightNos ?? []).join(', '));

    // Empty flight numbers always mean "all flights", regardless of the exclude flag.
    const initialFlightScope = (props.editItem?.FlightNos ?? []).length === 0
        ? 'all'
        : (props.editItem?.ExcludeFlightNos ? 'exclude' : 'include');
    const [ flightScope, setFlightScope] = useState<'all' | 'include' | 'exclude'>(initialFlightScope);
    const [ loadingTypes, setLoadingTypes] = useState<string[]>(props.editItem?.LoadingTypes ?? []);
    const [ waves, setWaves] = useState<number[]>(props.editItem?.Waves ?? []);
    const [ validFrom, setValidFrom] = useState<Dayjs | null>(props.editItem?.ValidFrom ? dayjs(props.editItem.ValidFrom) : null);
    const [ validTo, setValidTo] = useState<Dayjs | null>(props.editItem?.ValidTo ? dayjs(props.editItem.ValidTo) : null);

    const [ airlineCodeError, setAirlineCodeError] = useState(!isEdit);
    const [ depError, setDepError] = useState(!isEdit);
    const [ destinationGroupIdError, setDestinationGroupIdError] = useState(!isEdit);
    const [ loadingPlanIdError, setLoadingPlanIdError] = useState(!isEdit);
    const [ haulTypeError, setHaulTypeError] = useState(!isEdit);
    const [ catererIdError, setCatererIdError] = useState(!isEdit);

    const handleAirlineCode = (value : string | undefined) => {
        setAirlineCode(value ?? '');
        setAirlineCodeError(value === undefined || value === '');

        // Reset dependent selections when the airline changes
        setDep('');
        setDepError(true);
        setDestinationGroupId('');
        setDestinationGroupIdError(true);
        setLoadingPlanId('');
        setLoadingPlanIdError(true);
        setHaulType('');
        setHaulTypeError(true);
        setLoadingTypes([]);
    }

    const handleDep = (value : string | undefined) => {
        setDep(value ?? '');
        setDepError(value === undefined || value === '');
    }

    const handleDestinationGroupId = (value : string | undefined) => {
        setDestinationGroupId(value ?? '');
        setDestinationGroupIdError(value === undefined || value === '');
    }

    const handleLoadingPlanId = (value : string | undefined) => {
        setLoadingPlanId(value ?? '');
        setLoadingPlanIdError(value === undefined || value === '');
    }

    const handleHaulType = (value : string | undefined) => {
        setHaulType(value ?? '');
        setHaulTypeError(value === undefined || value === '');
    }

    const handleCatererId = (value : string | undefined) => {
        setCatererId(value ?? '');
        setCatererIdError(value === undefined || value === '');
    }

    const handleFlightNos = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFlightNos(e.target.value);
    }

    const handleFlightScope = (value : 'all' | 'include' | 'exclude') => {
        setFlightScope(value);

        if(value === 'all')
            setFlightNos('');
    }

    const dateRangeError = useMemo(() => {
        return validFrom !== null && validTo !== null && !validFrom.isBefore(validTo, 'day');
    }, [validFrom, validTo]);

    const hasErrors = useMemo(() => {
        return airlineCodeError || depError || destinationGroupIdError || loadingPlanIdError || haulTypeError || catererIdError || dateRangeError;
    }, [airlineCodeError, depError, destinationGroupIdError, loadingPlanIdError, haulTypeError, catererIdError, dateRangeError]);

    const waveLabel = (value : number) => props.waveList.find(o => o.value === value)?.label ?? String(value);

    return (
        <div>
        <Dialog open={props.open} onClose={props.cancelAction} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit loading plan rule' : 'Create loading plan rule'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ marginTop: 1 }}>
                    <FormControl sx={{minWidth: 200 }} size="small" disabled={isEdit}>
                        <InputLabel id="airline-label">Airline</InputLabel>
                        <Select
                            labelId="airline-label"
                            id="airline-select"
                            value={airlineCode}
                            error={airlineCodeError}
                            label="Airline"
                            onChange={(e) => handleAirlineCode(e.target.value) }>
                                { props.airlineList && props.airlineList.map((o)=> (
                                    <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Typography variant="overline" sx={{ fontWeight: 'bold' }}>1 - Scope of the rule</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small" disabled={isEdit || airlineCode === ''}>
                                    <InputLabel id="dep-label">Departure station</InputLabel>
                                    <Select
                                        labelId="dep-label"
                                        id="dep-select"
                                        value={dep}
                                        error={depError}
                                        label="Departure station"
                                        onChange={(e) => handleDep(e.target.value) }>
                                            { props.departureStationList.filter(o => o.airline === airlineCode).map((o)=> (
                                                <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small" disabled={airlineCode === ''}>
                                    <InputLabel id="destination-group-label">Destination group</InputLabel>
                                    <Select
                                        labelId="destination-group-label"
                                        id="destination-group-select"
                                        value={destinationGroupId}
                                        error={destinationGroupIdError}
                                        label="Destination group"
                                        onChange={(e) => handleDestinationGroupId(e.target.value) }>
                                            { props.destinationGroupList.filter(o => o.airline === airlineCode).map((o)=> (
                                                <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="caterer-label">Caterer</InputLabel>
                                    <Select
                                        labelId="caterer-label"
                                        id="caterer-select"
                                        value={catererId}
                                        error={catererIdError}
                                        label="Caterer"
                                        onChange={(e) => handleCatererId(e.target.value) }>
                                            { props.catererList && props.catererList.map((o)=> (
                                                <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>Flight scope</Typography>
                                <RadioGroup
                                    row
                                    value={flightScope}
                                    onChange={(e) => handleFlightScope(e.target.value as 'all' | 'include' | 'exclude')}
                                    sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1 }}
                                >
                                    {([
                                        { value: 'all', label: 'All flights' },
                                        { value: 'include', label: 'Include only selected flight numbers' },
                                        { value: 'exclude', label: 'Exclude selected flight numbers' },
                                    ] as const).map(o => (
                                        <FormControlLabel
                                            key={o.value}
                                            value={o.value}
                                            control={<Radio size="small" />}
                                            label={o.label}
                                            sx={{
                                                flex: 1,
                                                m: 0,
                                                px: 1.5,
                                                py: 1,
                                                border: '1px solid',
                                                borderColor: flightScope === o.value ? 'primary.main' : 'divider',
                                                borderRadius: 1,
                                                bgcolor: flightScope === o.value ? 'action.selected' : 'transparent',
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </Grid>
                            { flightScope !== 'all' && (
                                <Grid item xs={12}>
                                    <TextField
                                        type="text"
                                        variant='outlined'
                                        color='secondary'
                                        label={t('Flight numbers')}
                                        placeholder="e.g. 123, 456, 789"
                                        onChange={e => handleFlightNos(e)}
                                        value={flightNos}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Typography variant="caption" color="text.secondary">
                            { flightScope === 'all' && 'This rule applies to all flights.' }
                            { flightScope === 'include' && 'This rule applies only to the entered flight numbers.' }
                            { flightScope === 'exclude' && 'This rule applies to all flights except the entered flight numbers.' }
                            { ' A line with flight numbers is used before a line without flight numbers.' }
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="overline" sx={{ fontWeight: 'bold' }}>2 - What is loaded</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="loading-plan-label">Loading plan</InputLabel>
                                    <Select
                                        labelId="loading-plan-label"
                                        id="loading-plan-select"
                                        value={loadingPlanId}
                                        error={loadingPlanIdError}
                                        label="Loading plan"
                                        onChange={(e) => handleLoadingPlanId(e.target.value) }>
                                            { props.loadingPlanList.filter(o => o.airline === airlineCode).map((o)=> (
                                                <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="haul-type-label">Haul type</InputLabel>
                                    <Select
                                        labelId="haul-type-label"
                                        id="haul-type-select"
                                        value={haulType}
                                        error={haulTypeError}
                                        label="Haul type"
                                        onChange={(e) => handleHaulType(e.target.value) }>
                                            { props.haulTypeList.filter(o => o.airline === airlineCode).map((o)=> (
                                                <MenuItem  key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={props.loadingTypeList.filter(o => o.airline === airlineCode).map(o => o.value)}
                                    value={loadingTypes}
                                    onChange={(e, newValue) => setLoadingTypes(newValue)}
                                    renderInput={(params) => <TextField {...params} label="Service Type" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={props.waveList.map(o => o.value)}
                                    getOptionLabel={waveLabel}
                                    value={waves}
                                    onChange={(e, newValue) => setWaves(newValue)}
                                    renderInput={(params) => <TextField {...params} label="Waves" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary" component="div">Duty free</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Switch checked={isDutyFree} onChange={(e) => setIsDutyFree(e.target.checked)} />
                                    <Typography variant="body2">{isDutyFree ? 'Loaded' : 'Not loaded'}</Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" component="div">
                            Click to select. More than one value can be chosen.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="overline" sx={{ fontWeight: 'bold' }}>3 - Validity</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <ClearableDatePicker
                                    value={validFrom}
                                    label="Valid from"
                                    onChange={setValidFrom}
                                    error={dateRangeError}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ClearableDatePicker
                                    value={validTo}
                                    label="Valid to"
                                    onChange={setValidTo}
                                    error={dateRangeError}
                                    helperText={dateRangeError ? 'Valid to must be after valid from' : undefined}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.cancelAction}>Cancel</Button>
                <Button variant="contained" disabled={hasErrors} onClick={(e) => props.saveAction(
                    airlineCode,
                    dep,
                    destinationGroupId,
                    loadingPlanId,
                    haulType,
                    catererId,
                    isDutyFree,
                    flightScope === 'all' ? [] : flightNos.split(',').map(o => o.trim()).filter(o => o !== ''),
                    flightScope === 'exclude',
                    loadingTypes,
                    waves,
                    validFrom ? validFrom.format('YYYY-MM-DD') : undefined,
                    validTo ? validTo.format('YYYY-MM-DD') : undefined
                ) }>{isEdit ? 'Save rule' : 'Create rule'}</Button>
            </DialogActions>
      </Dialog>
      </div>
    )
}

export default LoadingPlanGroupDialog;
